import * as React from "react";
import styled, { css } from "styled-components";
import { select, Colorable, Color as ColorType } from "@hubper/theme";

// elements
import Text from "../Text";
import Icon, { IconName } from "../Icon";

import { ELEMENT_HEIGHT } from "../../constants";

const ELEMENT_HEIGHT_SMALL = 24;

/**
 * Styles
 */

export type ContainerProps = {
  square: boolean;
  stretch: boolean;
  cursor: string;
  small?: boolean;
  disabled?: boolean;
  "data-testid"?: string;
};

export const containerStyle = css<ContainerProps>`
  border: none;
  box-shadow: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${ELEMENT_HEIGHT}px;
  ${p =>
    !p.square
      ? css`
          padding: 0rem ${select.space(4)};
        `
      : css`
          width: ${ELEMENT_HEIGHT}px;
        `}

  ${p =>
    p.small &&
    css`
      height: ${ELEMENT_HEIGHT_SMALL}px;
      padding: 0 ${select.space(2)};
    `}
  transition: ${select.speed("fast")};
  border-radius: ${select.radius("normal")};
  font-weight: ${select.fontWeight("bold")};
  ${p => p.stretch && "width: 100%;"}
  cursor: ${p => p.cursor};
  opacity: ${p => (p.disabled ? 0.5 : 1)};

  & .icon {
    width: 16px;
    height: 16px;
  }

  & > *:not(:first-child) {
    margin-left: ${select.space(1)};
  }
`;

const Color = styled.button<Colorable & ContainerProps>`
  ${containerStyle}
  background-color: ${select.findColor};

  & ${Text} {
    color: white;
  }

  color: white;

  ${p =>
    !p.disabled &&
    css`
      &:hover {
        background-color: ${select.findColorAlterIndex(1)};
      }
    `}

`;

Color.defaultProps = {
  c: ["primary", 5]
};

const Grey = styled.button<ContainerProps>`
  ${containerStyle}
  background-color: ${select.color(["grey", 1])};
  
  & ${Text} {
    color: ${select.color(["grey", 6])};
  }

  color: ${select.color(["grey", 6])};

  ${p =>
    !p.disabled &&
    css`
      &:hover {
        background-color: ${select.color(["grey", 2])};
      }
    `}
`;

const Transparent = styled.button<ContainerProps>`
  ${containerStyle}
  background-color: transparent;

  & ${Text} {
    color: ${select.color(["grey", 6])};
  }

  color: ${select.color(["grey", 6])};

  ${p =>
    !p.disabled &&
    css`
      &:hover {
        background-color: ${select.color(["grey", 1])};
      }
    `}
`;

const Inverted = styled.button<Colorable & ContainerProps>`
  ${containerStyle}
  border: 1px solid ${select.findColor};
  background-color: transparent;

  & ${Text} {
    color: ${select.findColor};
  }

  color: ${select.findColor};

  ${p =>
    !p.disabled &&
    css`
      &:hover {
        color: ${select.findColorAlterIndex(-2)};
        border: 1px solid ${select.findColorAlterIndex(-2)};
        & ${Text} {
          color: ${select.findColorAlterIndex(-2)};
        }
      }
    `}
`;

Inverted.defaultProps = {
  c: ["primary", 5]
};

/**
 * Component
 */

type ButtonType = "grey" | "color" | "transparent" | "inverted";

export type ButtonProps = {
  type: ButtonType;
  text?: string;
  iconBefore?: IconName;
  iconAfter?: IconName;
  icon?: IconName;
  stretch?: boolean;
  c?: string | ColorType;
  disabled?: boolean;
  small?: boolean;
} & React.HTMLAttributes<HTMLButtonElement>;

export const Button = React.forwardRef((props: ButtonProps, ref: any) => {
  const {
    type,
    icon,
    iconAfter,
    iconBefore,
    text,
    c,
    stretch,
    disabled,
    small,
    ...nativeButtonProps
  } = props;

  function handleClick(evt: any) {
    if (disabled || !nativeButtonProps.onClick) {
      return;
    }
    nativeButtonProps.onClick(evt);
  }

  // @ts-ignore
  const hasIcon = icon && icon !== "none";

  let innerContent = null;
  if (hasIcon) {
    innerContent = <Icon data-testid="button-icon" name={icon as any} />;
  } else {
    innerContent = (
      <>
        {iconBefore && (
          <Icon data-testid="button-icon-before" name={iconBefore} />
        )}
        <Text
          data-testid="button-text"
          fs={-1}
          fw={"bold"}
          c="inherit"
          style={{ lineHeight: 1 }}
        >
          {text}
        </Text>
        {iconAfter && <Icon data-testid="button-icon-after" name={iconAfter} />}
      </>
    );
  }

  const containerProps: ContainerProps &
    React.HTMLAttributes<HTMLButtonElement> = {
    square: Boolean(hasIcon),
    stretch: stretch || false,
    cursor: nativeButtonProps.onClick && !disabled ? "pointer" : "default",
    disabled,
    small,
    ...nativeButtonProps,
    onClick: handleClick,
    "data-testid": "button"
  };

  switch (type) {
    case "color":
      return (
        <Color c={c} ref={ref} {...containerProps}>
          {innerContent}
        </Color>
      );
    case "grey":
      return (
        <Grey ref={ref} {...containerProps}>
          {innerContent}
        </Grey>
      );
    case "transparent":
      return (
        <Transparent ref={ref} {...containerProps}>
          {innerContent}
        </Transparent>
      );
    case "inverted":
      return (
        <Inverted c={c} ref={ref} {...containerProps}>
          {innerContent}
        </Inverted>
      );
    default:
      return null;
  }
});

export default Button;
